'use client';

import { useOnType } from '@/globals/helpers/useOnType';
import { searchIcon } from '@/uiIcons/searchIcon.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { icon } from '@/uiPrimitives/media/icon';
import { textBlock } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { useInteractOutside } from '@/globals/helpers/useInteractOutside';
import { SuggestType, SourceDataItem } from './Autocomplete.types';
import { pile } from '@/uiPrimitives/layout/pile';
import { hourglassIcon } from '@/uiIcons/hourglassIcon.css';
import { AutocompleteSuggestions } from './AutocompleteSuggestions';
import { position } from '@/uiPrimitives/layout/position';
import { ErrorMessage } from '@/sections/errorMessage/ErrorMessage';
import { Translation } from '@/globals/translate/translations';
import { stack } from '@/uiPrimitives/layout/stack';
import { useFormStatus } from 'react-dom';
import { AutocompleteFormState } from './Autocomplete';
import { ChangeEvent, FocusEvent, Ref, useRef, useActionState } from 'react';

export type AutocompleteProps = {
	suggestType: SuggestType;
	basePath?: string;
	size?: number;
	sourceData?: Array<SourceDataItem>;
	placeholder: string;
	noDataErrorMessage?: Translation<'noDataError'>;
	handleSearch: (
		previousState: AutocompleteFormState,
		formData: FormData,
	) => AutocompleteFormState | Promise<AutocompleteFormState>;
};

const AutocompleteInnerForm = ({
	inputRef,
	inputProps,
	formState,
	placeholder,
}: {
	inputRef: Ref<HTMLInputElement>;
	inputProps: {
		onChange: (e: ChangeEvent<HTMLInputElement>) => void;
		onBlur: (e: FocusEvent<HTMLInputElement, Element>) => void;
	};
	formState: AutocompleteFormState;
	placeholder: string;
}) => {
	const status = useFormStatus();

	return (
		<>
			<div className={classes(pile({ alignItems: 'center' }))}>
				<span
					className={classes(
						occupy({ inlineSize: '7 | betweenH3Sections' }),
						zIndex({ zIndex: '1' }),
					)}
				>
					<i
						className={classes(
							icon({
								icon: status.pending ? hourglassIcon : searchIcon,
								blockSize: '3 | h1',
							}),
						)}
					/>
				</span>
				<input
					{...inputProps}
					ref={inputRef}
					type="search"
					name="term"
					autoCapitalize="none"
					autoComplete="off"
					autoCorrect="off"
					spellCheck="false"
					defaultValue={formState.term}
					placeholder={placeholder}
					className={classes(
						textBlock({ color: 'positive:noHover' }),
						box({
							paddingBlock: '3 | h1',
							paddingInlineEnd: indentSize,
							paddingInlineStart: '7 | betweenH3Sections',
							backgroundColor: 'negative',
							borderWidth: '1px',
							borderStyle: 'solid',
							borderColor: 'positive',
						}),
					)}
					{...addTestSelector('uiSearchInput')}
				/>
			</div>
			{!status.pending && (
				<AutocompleteSuggestions suggestions={formState.suggestions} />
			)}
		</>
	);
};

export const AutocompleteClient = ({
	placeholder,
	noDataErrorMessage,
	...props
}: AutocompleteProps) => {
	// Don't destructure handleSearch; ts/vscode don't understand it is a valid import of a server action
	const [formState, formAction] = useActionState(props.handleSearch, {
		term: '',
		suggestions: [],
	});
	const inputRef = useRef<HTMLInputElement>(null);

	const { ref: formRef } = useInteractOutside<HTMLFormElement>(() => {
		if (
			formState.term !== '' &&
			inputRef.current != null &&
			formRef.current != null
		) {
			inputRef.current.value = '';
			formRef.current.requestSubmit();
		}
	});

	const { formProps, inputProps } = useOnType(
		{
			onChange: () => {
				if (formRef.current != null) formRef.current.requestSubmit();
			},
			action: formAction,
		},
		400,
	);

	return (
		<div className={classes(stack({ rowGap: '2 | h2' }))}>
			<form
				{...formProps}
				ref={formRef}
				action={formAction}
				id="autocompleteForm"
				className={classes(
					zIndex({ zIndex: '1' }),
					position({ position: 'relative' }),
				)}
				tabIndex={-1}
			>
				<AutocompleteInnerForm
					inputRef={inputRef}
					formState={formState}
					inputProps={inputProps}
					placeholder={placeholder}
				/>
			</form>
			{formState.error != null && noDataErrorMessage != null && (
				<ErrorMessage>{noDataErrorMessage}</ErrorMessage>
			)}
		</div>
	);
};
